import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import HdiRegistration from "./pages/HdiRegistration";
import HdiInspection from "./pages/HdiInspection";
import ModuleRegistration from "./pages/ModuleRegistration";
import EditModule from "./pages/EditModule";
import Help from "./pages/Help";

const EMPTY_DCA_STATE = {
  checked: false,
  loading: false,
  required: false,
  active: false,
  username: null,
  expires_at: null,
  login_type: null,
  error: null,
};

function App() {
  const auth = useAuth();
  const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [dcaSession, setDcaSession] = useState(EMPTY_DCA_STATE);
  const [dcaUsername, setDcaUsername] = useState("");
  const dcaPasswordInputRef = useRef(null);
  const dcaOtpInputRef = useRef(null);

  useEffect(() => {
    console.log("Auth state changed:", {
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      activeNavigator: auth.activeNavigator,
      user: auth.user,
      error: auth.error,
    });
  }, [auth.isAuthenticated, auth.isLoading, auth.activeNavigator, auth.user, auth.error]);

  const fetchBackendData = useCallback(() => {
    if (!auth.user?.access_token) return;

    fetch(`${apiUrl}/api/me`, {
      headers: {
        Authorization: `Bearer ${auth.user.access_token}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
      mode: "cors",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setUserData(data);
        setError(null);
      })
      .catch((err) => {
        setError(err.message);
        setUserData(null);
        setDcaSession(EMPTY_DCA_STATE);
      });
  }, [apiUrl, auth.user?.access_token]);

  const refreshDcaSession = useCallback(async () => {
    if (!auth.user?.access_token) {
      setDcaSession(EMPTY_DCA_STATE);
      return;
    }

    setDcaSession((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch(`${apiUrl}/api/v1/dca/session`, {
        headers: {
          Authorization: `Bearer ${auth.user.access_token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(`Failed to check DCA session: ${response.status}`);
      }

      const data = await response.json();
      setDcaSession({
        checked: true,
        loading: false,
        required: Boolean(data.required),
        active: Boolean(data.active),
        username: data.username || null,
        expires_at: data.expires_at || null,
        login_type: data.login_type || null,
        error: null,
      });
    } catch (err) {
      setDcaSession({
        checked: true,
        loading: false,
        required: Boolean(userData?.roles?.includes("psi")),
        active: false,
        username: null,
        expires_at: null,
        login_type: null,
        error: err.message || "Failed to check DCA session.",
      });
    }
  }, [apiUrl, auth.user?.access_token, userData?.roles]);

  const handleDcaLogin = useCallback(
    async (event) => {
      event.preventDefault();
      if (!auth.user?.access_token) return;

      const formData = new FormData(event.currentTarget);
      const username = String(formData.get("username") || "").trim();
      const password = String(formData.get("password") || "");
      const otp = String(formData.get("otp") || "").trim();

      if (!username || !password) {
        return;
      }

      setDcaSession((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await fetch(`${apiUrl}/api/v1/dca/session/login`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          mode: "cors",
          body: JSON.stringify({
            username,
            password,
            otp,
          }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || `DCA login failed (${response.status})`);
        }

        setDcaSession({
          checked: true,
          loading: false,
          required: Boolean(data.required),
          active: Boolean(data.active),
          username: data.username || username || null,
          expires_at: data.expires_at || null,
          login_type: data.login_type || null,
          error: null,
        });
        setDcaUsername(username);
        if (dcaPasswordInputRef.current) dcaPasswordInputRef.current.value = "";
        if (dcaOtpInputRef.current) dcaOtpInputRef.current.value = "";
      } catch (err) {
        setDcaSession((prev) => ({
          ...prev,
          checked: true,
          loading: false,
          active: false,
          error: err.message || "DCA login failed.",
        }));
      }
    },
    [apiUrl, auth.user?.access_token]
  );

  const handleLogout = useCallback(async () => {
    if (auth.user?.access_token) {
      try {
        await fetch(`${apiUrl}/api/v1/dca/session`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
          },
          credentials: "include",
          mode: "cors",
        });
      } catch (err) {
        console.warn("Failed to clear DCA session before logout:", err);
      }
    }

    auth.signoutRedirect();
  }, [apiUrl, auth]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchBackendData();
    } else {
      setUserData(null);
      setError(null);
      setDcaSession(EMPTY_DCA_STATE);
      setDcaUsername("");
    }
  }, [auth.isAuthenticated, fetchBackendData]);

  useEffect(() => {
    if (auth.isAuthenticated && userData) {
      refreshDcaSession();
      setDcaUsername((prev) => prev || userData.username || "");
    } else if (!auth.isAuthenticated) {
      setDcaSession(EMPTY_DCA_STATE);
    }
  }, [auth.isAuthenticated, refreshDcaSession, userData]);

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }

  function HomePage() {
    const needsDca = Boolean(userData?.roles?.includes("psi"));

    return (
      <section className="page-section">
        <h2>Home</h2>
        {!auth.isAuthenticated ? (
          <p>Log in with CERN to use the application.</p>
        ) : (
          <>
            <p>
              Signed in as <strong>{userData?.name || auth.user?.profile.name || "User"}</strong>.
            </p>
            {needsDca ? (
              <p>
                DCA session:{" "}
                {dcaSession.active
                  ? `connected as ${dcaSession.username || userData?.username || "unknown"}`
                  : "not connected yet"}
              </p>
            ) : (
              <p>DCA session is not required for your current role.</p>
            )}
          </>
        )}
      </section>
    );
  }

  function DcaBootstrapPage() {
    if (!dcaSession.checked || dcaSession.loading) {
      return (
        <section className="page-section page-section--compact">
          <h2>Connecting To DCA</h2>
          <p>Checking whether a DCA session already exists for this browser session.</p>
        </section>
      );
    }

    return (
      <section className="page-section page-section--compact dca-gate">
        <h2>DCA Login Required</h2>
        <p>
          This CERN account is already signed in to the application. To do database lookups and
          uploads, we also need a DCA session on the backend for this browser session.
        </p>
        <p>
          The password and OTP are used only to bootstrap the DCA session and are not stored. Enter
          the 6-digit code here if your CERN login supports TOTP.
        </p>
        <form className="dca-form" onSubmit={handleDcaLogin} autoComplete="on">
          <label className="form-field" htmlFor="dca-username">
            DCA Username
            <input
              className="form-input"
              id="dca-username"
              name="username"
              type="text"
              value={dcaUsername}
              onChange={(event) => setDcaUsername(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
            />
          </label>
          <label className="form-field" htmlFor="dca-password">
            Password
            <input
              className="form-input"
              id="dca-password"
              name="password"
              type="password"
              ref={dcaPasswordInputRef}
              autoComplete="current-password"
              spellCheck={false}
              required
            />
          </label>
          <label className="form-field" htmlFor="dca-otp">
            One-Time Code (6-digit TOTP, if enabled)
            <input
              className="form-input"
              id="dca-otp"
              name="otp"
              type="text"
              ref={dcaOtpInputRef}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </label>
          {dcaSession.error && (
            <p className="form-error" role="alert">
              {dcaSession.error}
            </p>
          )}
          <button
            className="primary-button"
            type="submit"
            disabled={!dcaUsername || dcaSession.loading}
          >
            {dcaSession.loading ? "Connecting..." : "Connect DCA"}
          </button>
        </form>
      </section>
    );
  }

  function CallbackHandler({ onComplete }) {
    const callbackAuth = useAuth();
    const navigate = useNavigate();
    const [localError, setLocalError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const callbackApiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

      if (callbackAuth.isLoading) return;

      if (!callbackAuth.isAuthenticated) {
        setLocalError("Authentication failed or was cancelled.");
        setLoading(false);
        return;
      }

      fetch(`${callbackApiUrl}/api/me`, {
        headers: {
          Authorization: `Bearer ${callbackAuth.user?.access_token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        mode: "cors",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (onComplete) onComplete(data);

          const redirectFromState =
            callbackAuth.user?.state &&
            typeof callbackAuth.user.state === "object" &&
            typeof callbackAuth.user.state.returnTo === "string"
              ? callbackAuth.user.state.returnTo
              : null;
          const redirectFromStorage = sessionStorage.getItem("post_login_redirect");
          const returnTo = redirectFromState || redirectFromStorage;
          const isSafeInternalPath =
            typeof returnTo === "string" &&
            returnTo.startsWith("/") &&
            !returnTo.startsWith("/callback");

          sessionStorage.removeItem("post_login_redirect");
          navigate(isSafeInternalPath ? returnTo : "/");
        })
        .catch((err) => {
          setLocalError(err.message || "Failed to fetch backend user info.");
        })
        .finally(() => setLoading(false));
    }, [
      callbackAuth.isAuthenticated,
      callbackAuth.isLoading,
      callbackAuth.user,
      navigate,
      onComplete,
    ]);

    if (loading) return <div>Processing login...</div>;
    if (localError) {
      return (
        <div>
          <p style={{ color: "red" }}>Login processing error: {localError}</p>
          <button onClick={() => navigate("/")}>Go home</button>
        </div>
      );
    }
    return null;
  }

  function MePage() {
    if (!auth.isAuthenticated) {
      return (
        <section className="page-section">
          <h2>Backend User Info</h2>
          <p>You must be logged in to view this page.</p>
        </section>
      );
    }

    return (
      <section className="page-section">
        <h2>User Profile</h2>
        {userData ? (
          <div className="profile-panel">
            <div className="profile-row">
              <strong>Name:</strong> {userData.name || "N/A"}
            </div>
            <div className="profile-row">
              <strong>Username:</strong> {userData.username || "N/A"}
            </div>
            <div className="profile-row">
              <strong>Email:</strong> {userData.email || "N/A"}
            </div>
            <div className="profile-row">
              <strong>Role:</strong>{" "}
              {userData.roles && userData.roles.length > 0
                ? userData.roles.join(", ")
                : "default-role"}
            </div>
            <div className="profile-row">
              <strong>DCA Session:</strong>{" "}
              {dcaSession.required
                ? dcaSession.active
                  ? `Connected as ${dcaSession.username || "N/A"}`
                  : "Not connected"
                : "Not required"}
            </div>
            <details className="debug-details">
              <summary className="debug-summary">Raw User Data (JSON)</summary>
              <pre className="debug-pre">{JSON.stringify(userData, null, 2)}</pre>
            </details>
          </div>
        ) : (
          <p>Loading data from backend... {error && `Error: ${error}`}</p>
        )}
      </section>
    );
  }

  function Content() {
    const location = useLocation();
    const commitHash =
      window.APP_CONFIG?.REACT_APP_COMMIT_HASH || process.env.REACT_APP_COMMIT_HASH;
    const buildDate = window.APP_CONFIG?.REACT_APP_BUILD_DATE || process.env.REACT_APP_BUILD_DATE;
    const commitBaseUrl = "https://gitlab.cern.ch/psi/cms-it-module-mgmt/-/commit";
    const hasPsiRole = Boolean(userData?.roles?.includes("psi"));
    const needsDca = Boolean(auth.isAuthenticated && hasPsiRole);
    const currentPath = location.pathname;
    const routeRequiresDca = ["/hdi-registration", "/module-registration", "/edit-module"].some(
      (path) => currentPath === path || currentPath.startsWith(`${path}/`)
    );
    const showDcaGate =
      needsDca &&
      routeRequiresDca &&
      (!dcaSession.checked || dcaSession.loading || !dcaSession.active);
    const PsiRoute = ({ children }) => {
      if (!auth.isAuthenticated) {
        return children;
      }
      if (!userData) {
        return (
          <section className="page-section page-section--compact">
            <h2>Loading Access</h2>
            <p>Checking your account details.</p>
            {error && <p className="error-message">{error}</p>}
          </section>
        );
      }
      if (!hasPsiRole) {
        return (
          <section className="page-section page-section--compact">
            <h2>PSI Access Required</h2>
            <p>Your account does not have the PSI role required for this page.</p>
          </section>
        );
      }
      return children;
    };

    return (
      <div className="App app-shell">
        <header className="app-header">
          <div className="app-brand-group">
            <Link to="/" className="app-title-link">
              <h1>CMS IT Module Management</h1>
            </Link>
            <nav className="app-nav" aria-label="Primary">
              <Link to="/" className="app-link">
                Home
              </Link>
              <Link to="/hdi-inspection" className="app-link">
                HDI Inspection
              </Link>
              {hasPsiRole && (
                <>
                  <Link to="/hdi-registration" className="app-link">
                    HDI Registration
                  </Link>
                  <Link to="/module-registration" className="app-link">
                    Module Registration
                  </Link>
                  <Link to="/edit-module" className="app-link">
                    Edit Module
                  </Link>
                </>
              )}
            </nav>
          </div>

          <div className="app-actions">
            {needsDca && dcaSession.active && (
              <span className="dca-status-pill">
                DCA: {dcaSession.username || userData?.username || "connected"}
              </span>
            )}
            <Link to="/help" className="app-link app-link--subtle">
              Help
            </Link>
            {auth.isAuthenticated ? (
              <>
                <Link to="/me" className="app-link app-link--subtle">
                  {auth.user?.profile.name || "User"}
                </Link>
                <button className="secondary-button" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <button
                className="primary-button"
                onClick={() => {
                  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                  sessionStorage.setItem("post_login_redirect", returnTo);
                  auth.signinRedirect({ state: { returnTo } });
                }}
              >
                Log in with CERN
              </button>
            )}
          </div>
        </header>

        <main className="app-main">
          {showDcaGate ? (
            <DcaBootstrapPage />
          ) : (
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/callback" element={<CallbackHandler onComplete={setUserData} />} />
              <Route path="/me" element={<MePage />} />
              <Route path="/hdi-inspection" element={<HdiInspection />} />
              <Route path="/help" element={<Help />} />
              <Route
                path="/hdi-registration"
                element={
                  <PsiRoute>
                    <HdiRegistration />
                  </PsiRoute>
                }
              />
              <Route
                path="/module-registration"
                element={
                  <PsiRoute>
                    <ModuleRegistration />
                  </PsiRoute>
                }
              />
              <Route
                path="/edit-module"
                element={
                  <PsiRoute>
                    <EditModule />
                  </PsiRoute>
                }
              />
            </Routes>
          )}
        </main>

        <footer className="app-footer">
          <p className="app-footer-text">
            Commit:&nbsp;
            {commitHash ? (
              <a
                href={`${commitBaseUrl}/${commitHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link"
              >
                {commitHash.slice(0, 7)}
              </a>
            ) : (
              "N/A"
            )}{" "}
            | Built: {buildDate || "N/A"}
          </p>
        </footer>
      </div>
    );
  }

  return (
    <Router>
      <Content />
    </Router>
  );
}

export default App;
